import { inject, Injectable, signal } from '@angular/core'
import { firstValueFrom } from 'rxjs'

import { Circuit, CircuitResume } from '@core/interfaces/circuits.interface'
import { CircuitsService } from '@core/services/circuits.service'
import { ErrorService } from '@core/services/error/error.service'
import { USER_TYPES } from '@core/interfaces/participant.interface'

@Injectable({
  providedIn: 'root'
})
export class CircuitsStore {
  #circuitsService = inject(CircuitsService)
  #errorService = inject(ErrorService)

  #circuits = signal<Circuit[]>([])
  circuits = this.#circuits.asReadonly()

  #circuitsSummaries = signal<CircuitResume[]>([])
  circuitsSummaries = this.#circuitsSummaries.asReadonly()

  /**
   * Carga los circuitos para un tipo de usuario específico
   * Si ya están cargados circuitos para ese tipo, no realiza la solicitud
   *
   * @param {USER_TYPES} useType - Tipo de usuario para el cual cargar circuitos
   * @returns {Promise<void>}
   */
  async loadCircuits(useType: USER_TYPES) {
    if (this.checkLoadedCircuitsByUserType(useType)) {
      return this.circuits()
    }

    try {
      const category = `circuitos_${useType}`
      const circuits$ = this.#circuitsService.fetchCircuits(category, 1)
      const circuits = await firstValueFrom(circuits$)

      this.setCircuits(circuits)
      return circuits
    } catch (error) {
      this.#errorService.handleError(error)
      return []
    }
  }

  /**
   * Verifica si ya hay circuitos cargados para un tipo de usuario específico
   *
   * @param {string} useType - Tipo de usuario a verificar
   * @returns {boolean} true si ya existen circuitos cargados para ese tipo de usuario
   * @private
   */
  private checkLoadedCircuitsByUserType(useType: string) {
    const circuits = this.#circuits()
    return circuits.some(circuit => circuit.user_type === useType)
  }

  /**
   * Establece los circuitos en el store, asegurando que no haya duplicados
   * utilizando un mapa con claves únicas por ID de circuito
   *
   * @param {Circuit[]} circuits - Array de circuitos a almacenar
   */
  setCircuits(circuits: Circuit[]) {
    const existing = this.#circuits()
    const circuitMap = new Map<string, Circuit>()

    existing.forEach(circuit => {
      circuitMap.set(circuit.slug, circuit)
    })

    circuits.forEach(circuit => {
      circuitMap.set(circuit.slug, circuit)
    })

    this.#circuits.set(Array.from(circuitMap.values()))
  }
}
